/**
 * 422 від сервера → підписи біля конкретних полів форми.
 *
 * Сервер уже давно віддає структуровану помилку:
 *
 *   {"error":{"code":"validation-error","details":{"errors":[
 *      {"loc":["body","name"],"msg":"Field required","type":"missing"}]}}}
 *
 * А фронт її ніде не розбирав: перехоплювач навмисно мовчить на 422 («форма
 * сама обробить»), і форма справді нічого не показувала. Людина натискала
 * «Зберегти», нічого не відбувалось, і зрозуміти чому було неможливо.
 *
 * Тексти від pydantic англійські й технічні («Input should be a valid
 * integer»). Перекладаємо найчастіші; для решти лишається сам текст — це
 * гірше за переклад, але краще за мовчання.
 */

/** Ключ — назва поля з `loc`, значення — готовий підпис українською. */
export type FieldErrors = Record<string, string>;

const BY_TYPE: Record<string, string> = {
  missing: "Заповніть це поле",
  string_too_short: "Замало символів",
  string_too_long: "Забагато символів",
  greater_than: "Число має бути більшим",
  greater_than_equal: "Число замале",
  less_than: "Число має бути меншим",
  less_than_equal: "Число завелике",
  int_parsing: "Потрібне ціле число",
  float_parsing: "Потрібне число",
  decimal_parsing: "Потрібне число",
  date_parsing: "Потрібна дата",
  value_error: "Значення не підходить",
  enum: "Оберіть значення зі списку",
};

interface RawError {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

/**
 * Витягти помилки полів з відповіді axios.
 *
 * Порожній обʼєкт означає «це не 422 про поля» — тоді форма показує загальне
 * повідомлення, як і раніше.
 */
export function fieldErrors(err: unknown): FieldErrors {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const errors = (
    data as { error?: { details?: { errors?: RawError[] } } } | undefined
  )?.error?.details?.errors;
  if (!Array.isArray(errors)) return {};

  const out: FieldErrors = {};
  for (const e of errors) {
    // `loc` — це шлях: ["body", "material_lines", 0, "qty"]. Береться
    // ОСТАННІЙ сегмент-рядок: саме він і є назвою поля у формі.
    const path = (e.loc ?? []).filter((x) => typeof x === "string") as string[];
    const name = path.length ? path[path.length - 1] : "";
    if (!name || name === "body") continue;
    if (out[name]) continue; // перша помилка на полі — найзрозуміліша
    out[name] = BY_TYPE[e.type ?? ""] ?? e.msg ?? "Значення не підходить";
  }
  return out;
}

/**
 * Один рядок для випадків, де підсвітити поле нема куди (наприклад помилка
 * стосується всього тіла запиту).
 */
export function firstFieldError(err: unknown): string | null {
  const all = fieldErrors(err);
  const keys = Object.keys(all);
  return keys.length ? `${keys[0]}: ${all[keys[0]]}` : null;
}

/**
 * Код помилки з конверта (`{"error":{"code":"shift_closed_today"}}`).
 *
 * Тут же читає й перехоплювач у `api/client.ts`, і екрани, які показують
 * причину НА СОБІ (смуга зміни, вхід) — щоб рядок-код був написаний в
 * одному місці, а не в трьох.
 */
export function errorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const code = (data as { error?: { code?: string } } | undefined)?.error?.code;
  return code ?? null;
}

/**
 * Текст помилки з конверта — уже українською, бо його пише бек.
 *
 * `fallback` повертається, коли конверта немає (offline, CORS, 502 від
 * проксі). Свідомо НЕ підставляємо `err.message` від axios: це англійське
 * «Request failed with status code 403», яке швеї нічого не пояснює.
 */
export function errorMessage(err: unknown, fallback = "Невідома помилка"): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  // Відмова валідації — назвати ПОЛЕ, а не «Request validation failed».
  // Куплено 01.10.2026: калькуляцію не зберігали шість разів поспіль, бо був
  // порожній курс, а екран казав англійською, що «запит не пройшов».
  if (errorCode(err) === "validation-error") {
    const why = validationSummary(err);
    if (why) return `Не збережено: ${why}`;
  }
  const msg = (data as { error?: { message?: string } } | undefined)?.error?.message;
  return msg && msg.trim() ? msg : fallback;
}

/** Людські назви частих полів. Невідоме поле лишається своєю назвою —
 *  гірше за переклад, але все одно вказує, ДЕ помилка. */
const FIELD_LABELS: Record<string, string> = {
  exchange_rate: "Курс",
  currency: "Валюта",
  rate: "Курс",
  name: "Назва",
  qty: "Кількість",
  quantity: "Кількість",
  unit: "Одиниця",
  unit_price_usd: "Ціна",
  amount_grn: "Сума, грн",
  amount: "Сума",
  overhead_grn: "Поточні витрати",
  sale_price_grn: "Ціна продажу",
  total: "Кількість",
  color: "Колір",
  size: "Розмір",
  comment: "Коментар",
  reason: "Причина",
  phone: "Телефон",
  email: "Пошта",
  full_name: "ПІБ",
  date: "Дата",
};

const LIST_LABELS: Record<string, string> = {
  material_lines: "Матеріал",
  operation_lines: "Операція",
  items: "Позиція",
  cells: "Клітинка",
  lines: "Рядок",
  rows: "Рядок",
};

/** «Матеріал №2: «Назва» — заповніть це поле» з першої помилки валідації. */
export function validationSummary(err: unknown): string | null {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const errors = (
    data as { error?: { details?: { errors?: RawError[] } } } | undefined
  )?.error?.details?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const e = errors[0];
  const loc = (e.loc ?? []).filter((x) => x !== "body");
  let where = "";
  let field = "";
  for (let i = 0; i < loc.length; i++) {
    const seg = loc[i];
    if (typeof seg === "number") continue;
    const next = loc[i + 1];
    if (typeof next === "number" && LIST_LABELS[seg]) {
      where = `${LIST_LABELS[seg]} №${next + 1}: `;
    } else {
      field = seg;
    }
  }
  const text = BY_TYPE[e.type ?? ""] ?? e.msg ?? "значення не підходить";
  const label = field ? `«${FIELD_LABELS[field] ?? field}» — ` : "";
  const more = errors.length > 1 ? ` (і ще ${errors.length - 1})` : "";
  return `${where}${label}${text.charAt(0).toLowerCase()}${text.slice(1)}${more}`;
}
